import React, { useState } from 'react';
import { Star, MapPin, MessageSquare, Video, Clock, Heart, Share2, Award, ChevronDown, Globe, Zap, GraduationCap, ShieldCheck, Crown, Gem, BadgeCheck, Users, ArrowRight, ShoppingBag, FileText, PlayCircle, BookOpen, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Rating } from '../components/ui/Rating';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { AdSlot } from '../components/ads/AdSlot';
interface AstrologerProfilePageProps {
  onNavigate: (page: string) => void;
}
function BadgePill({
  badge
}: {
  badge: string;
}) {
  let icon: React.ReactNode, text: string, cls: string, tooltip: string;
  if (badge === 'TOP') {
    icon = <Crown className="w-2.5 h-2.5" data-id="element-17041" />;
    text = 'TOP';
    cls = 'bg-amber-100 text-amber-700 border-amber-200';
    tooltip = 'TOP — лучший рейтинг и отзывы';
  } else if (badge === 'PRO') {
    icon = <ShieldCheck className="w-2.5 h-2.5" data-id="element-17042" />;
    text = 'PRO';
    cls = 'bg-indigo-100 text-indigo-700 border-indigo-200';
    tooltip = 'PRO — подтверждённый профессионал';
  } else {
    icon = <Gem className="w-2.5 h-2.5" data-id="element-17043" />;
    text = 'Рек.';
    cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    tooltip = 'Рекомендуем — выбор редакции';
  }
  return <div className="relative group/pill inline-flex" data-id="element-17044">
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border cursor-help ${cls}`} data-id="element-17045">
        {icon}
        {text}
      </span>
      <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 invisible group-hover/pill:opacity-100 group-hover/pill:visible transition-all duration-150 pointer-events-none z-50 shadow-lg" data-id="element-17046">
        {tooltip}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-17047" />
      </div>
    </div>;
}
function ReviewText({
  text,
  maxLength = 150
}: {
  text: string;
  maxLength?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= maxLength) return <p className="text-slate-600 text-sm leading-relaxed" data-id="element-17048">{text}</p>;
  return <p className="text-slate-600 text-sm leading-relaxed" data-id="element-17049">
      {expanded ? text : text.slice(0, maxLength) + '...'}
      <button onClick={e => {
      e.stopPropagation();
      setExpanded(!expanded);
    }} className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium text-xs" data-id="element-17050">
        {expanded ? 'Свернуть' : 'Читать далее'}
      </button>
    </p>;
}
export function AstrologerProfilePage({
  onNavigate
}: AstrologerProfilePageProps) {
  const {
    addItem
  } = useCart();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedTime, setSelectedTime] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(9);
  const [currentYear, setCurrentYear] = useState(2025);
  const [shareToast, setShareToast] = useState(false);
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт'];
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDay(0);
  };
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDay(0);
  };
  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
    }
    setShareToast(true);
    setTimeout(() => setShareToast(false), 2000);
  };
  const baseDayNum = 14;
  const availableDays = dayNames.map((name, i) => ({
    label: name,
    num: baseDayNum + i
  }));
  const timeSlots = ['10:00', '12:30', '15:00', '18:00'];
  const services = [{
    title: 'Разбор натальной карты',
    price: '5 000 ₽',
    duration: '60 мин',
    format: 'video' as const,
    desc: 'Полный анализ карты: сильные стороны, предназначение, финансы и отношения.'
  }, {
    title: 'Соляр на год',
    price: '4 000 ₽',
    duration: '3 дня',
    format: 'chat' as const,
    desc: 'Текстовый прогноз на год с разбором ключевых сфер жизни.'
  }, {
    title: 'Синастрия (Совместимость)',
    price: '6 000 ₽',
    duration: '90 мин',
    format: 'video' as const,
    desc: 'Анализ совместимости партнеров, точки роста и рекомендации.'
  }];
  const products = [{
    id: 1,
    title: 'Гид «Ретроградный Меркурий»',
    type: 'PDF',
    price: '990 ₽',
    icon: FileText,
    color: 'bg-blue-100 text-blue-600'
  }, {
    id: 2,
    title: 'Вебинар «Натальная карта для начинающих»',
    type: 'Видео',
    price: '2 500 ₽',
    icon: PlayCircle,
    color: 'bg-purple-100 text-purple-600'
  }, {
    id: 3,
    title: 'Рабочая тетрадь «Лунный календарь 2025»',
    type: 'PDF',
    price: '1 200 ₽',
    icon: BookOpen,
    color: 'bg-emerald-100 text-emerald-600'
  }];
  const reviews = [{
    id: 1,
    name: 'Мария К.',
    date: '12 мая 2024',
    service: 'Натальная карта',
    rating: 5,
    text: 'Елена — потрясающий специалист! Очень глубокий разбор, всё по полочкам. Никакой воды, только конкретика. Ушла с полным пониманием, куда двигаться дальше. Обязательно вернусь за соляром!'
  }, {
    id: 2,
    name: 'Алексей Д.',
    date: '3 мая 2024',
    service: 'Синастрия',
    rating: 5,
    text: 'Заказывали с женой синастрию. Елена очень тактично и профессионально разобрала наши сильные и слабые стороны как пары. Многое стало понятнее, появились конкретные инструменты для работы над отношениями. Рекомендую всем парам, даже если кажется что всё хорошо — узнаете много нового.'
  }, {
    id: 3,
    name: 'Ольга В.',
    date: '28 апр 2024',
    service: 'Соляр на год',
    rating: 4,
    text: 'Хороший прогноз, всё структурировано. Единственное — хотелось бы чуть больше деталей по финансовой сфере.'
  }, {
    id: 4,
    name: 'Дмитрий Р.',
    date: '15 апр 2024',
    service: 'Натальная карта',
    rating: 5,
    text: 'Первый раз обращался к астрологу и не пожалел. Елена объясняет простым языком, без эзотерического тумана. Получил чёткие рекомендации по карьере.'
  }, {
    id: 5,
    name: 'Наталья С.',
    date: '2 апр 2024',
    service: 'Детский гороскоп',
    rating: 5,
    text: 'Заказала разбор карты для дочери. Очень полезно — теперь лучше понимаю её характер и как поддержать в учёбе.'
  }];
  const faqs = [{
    id: '1',
    title: 'Как проходит видео-консультация?',
    content: 'Созваниваемся в Zoom/Google Meet. Я заранее готовлю карту. Во время звонка разбираем все аспекты, после — отправляю запись и рекомендации.'
  }, {
    id: '2',
    title: 'Что нужно для подготовки?',
    content: 'Дата, время и место рождения. Если время неизвестно — проведём ректификацию.'
  }, {
    id: '3',
    title: 'Можно ли перенести запись?',
    content: 'Да, не позднее чем за 24 часа. При отмене менее чем за сутки предоплата не возвращается.'
  }];
  const similarAstrologers = [{
    id: 1,
    name: 'Михаил Волков',
    rating: 4.8,
    reviews: 86,
    price: 4500,
    badge: 'PRO',
    avatar: 'bg-amber-100 text-amber-700'
  }, {
    id: 2,
    name: 'Анна Коваль',
    rating: 5.0,
    reviews: 214,
    price: 6000,
    badge: 'TOP',
    avatar: 'bg-emerald-100 text-emerald-700'
  }, {
    id: 3,
    name: 'Дмитрий Орлов',
    rating: 4.9,
    reviews: 152,
    price: 5500,
    badge: 'Featured',
    avatar: 'bg-blue-100 text-blue-700'
  }];
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  return <div className="w-full bg-slate-50 pb-20 min-h-screen" data-id="element-17051">
      <div className="h-40 md:h-52 bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 w-full relative" data-id="element-17052">
        <div className="absolute inset-0 bg-black/10" data-id="element-17053" />
        <div className="absolute top-3 left-4 sm:left-6 lg:left-8 z-10" data-id="element-17054">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астрологи',
          onClick: () => onNavigate('astrologers')
        }, {
          label: 'Елена Смирнова'
        }]} className="text-white/80 [&_span]:text-white [&_button]:text-white/80 [&_button:hover]:text-white [&_svg]:text-white/50" data-id="element-17055" />
        </div>
      </div>

      {shareToast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-17056">
          Ссылка скопирована ✓
        </div>}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20" data-id="element-17057">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-17058">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-5" data-id="element-17059">
            {/* Profile Header */}
            <SectionErrorBoundary sectionName="astrologer-hero" data-id="element-17060">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-visible" data-id="element-17061">
                <div className="px-4 sm:px-6" data-id="element-17062">
                  {/* Mobile layout */}
                  <div className="sm:hidden flex flex-col items-center pt-16" data-id="element-17063">
                    <div className="relative mb-3 -mt-28" data-id="element-17064">
                      <div className="w-24 h-24 rounded-2xl bg-white p-1 shadow-lg" data-id="element-17065">
                        <div className="w-full h-full rounded-xl bg-indigo-100 flex items-center justify-center text-3xl font-bold text-indigo-700" data-id="element-17066">
                          ЕС
                        </div>
                      </div>
                      <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-[3px] border-white rounded-full z-20" title="Онлайн" data-id="element-17067" />
                    </div>
                    <div className="text-center mb-2" data-id="element-17068">
                      <h1 className="text-xl font-bold text-slate-900 flex items-center justify-center gap-1.5 mb-1" data-id="element-17069">
                        Елена Смирнова
                        <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-17070" />
                      </h1>
                      <div className="flex items-center justify-center gap-1.5 mb-2" data-id="element-17071">
                        <BadgePill badge="TOP" data-id="element-17072" />
                        <BadgePill badge="PRO" data-id="element-17073" />
                      </div>
                      <div className="flex items-center justify-center gap-2 text-xs text-slate-500" data-id="element-17074">
                        <span className="flex items-center text-emerald-600 font-medium" data-id="element-17075">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" data-id="element-17076" />
                          Онлайн
                        </span>
                        <span className="text-slate-300" data-id="element-17077">•</span>
                        <span className="flex items-center" data-id="element-17078">
                          <Zap className="w-3 h-3 text-amber-500 mr-0.5" data-id="element-17079" />
                          ~15 мин
                        </span>
                        <span className="text-slate-300" data-id="element-17080">•</span>
                        <span className="flex items-center" data-id="element-17081">
                          <MapPin className="w-3 h-3 mr-0.5" data-id="element-17082" />
                          Москва
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full mb-4" data-id="element-17083">
                      <button onClick={() => setIsFavorite(!isFavorite)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-all ${isFavorite ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-slate-200 text-slate-600'}`} data-id="element-17084">
                        <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : ''}`} data-id="element-17085" />
                        {isFavorite ? 'В избранном' : 'В избранное'}
                      </button>
                      <button onClick={handleShare} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600" data-id="element-17086">
                        <Share2 className="w-4 h-4" data-id="element-17087" /> Поделиться
                      </button>
                    </div>
                  </div>

                  {/* Desktop layout */}
                  <div className="hidden sm:block pt-20" data-id="element-17088">
                    <div className="flex items-end gap-4 -mt-28 mb-6" data-id="element-17089">
                      <div className="relative flex-shrink-0" data-id="element-17090">
                        <div className="w-28 h-28 rounded-2xl bg-white p-1 shadow-lg" data-id="element-17091">
                          <div className="w-full h-full rounded-xl bg-indigo-100 flex items-center justify-center text-4xl font-bold text-indigo-700" data-id="element-17092">
                            ЕС
                          </div>
                        </div>
                        <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-[3px] border-white rounded-full z-20" title="Онлайн" data-id="element-17093" />
                      </div>
                      <div className="flex-1 min-w-0 pb-1" data-id="element-17094">
                        <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-17095">
                          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-1.5" data-id="element-17096">
                            Елена Смирнова
                            <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-17097" />
                          </h1>
                          <BadgePill badge="TOP" data-id="element-17098" />
                          <BadgePill badge="PRO" data-id="element-17099" />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-17100">
                          <span className="flex items-center text-emerald-600 font-medium" data-id="element-17101">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" data-id="element-17102" />
                            Онлайн
                          </span>
                          <span className="text-slate-300" data-id="element-17103">•</span>
                          <span className="flex items-center" data-id="element-17104">
                            <Zap className="w-3 h-3 text-amber-500 mr-0.5" data-id="element-17105" />
                            ~15 мин
                          </span>
                          <span className="text-slate-300" data-id="element-17106">•</span>
                          <span className="flex items-center" data-id="element-17107">
                            <MapPin className="w-3 h-3 mr-0.5" data-id="element-17108" />
                            Москва
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 pb-1 flex-shrink-0" data-id="element-17109">
                        <button onClick={() => setIsFavorite(!isFavorite)} className={`p-2 rounded-lg border transition-all ${isFavorite ? 'border-rose-200 bg-rose-50' : 'border-slate-200 hover:border-rose-200 hover:bg-rose-50'}`} title={isFavorite ? 'Убрать из избранного' : 'В избранное'} data-id="element-17110">
                          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} data-id="element-17111" />
                        </button>
                        <button onClick={handleShare} className="p-2 rounded-lg border border-slate-200 hover:border-slate-300 transition-all" title="Поделиться" data-id="element-17112">
                          <Share2 className="w-4 h-4 text-slate-400" data-id="element-17113" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-y border-slate-100 mb-4 text-center" data-id="element-17114">
                    <div data-id="element-17115">
                      <div className="flex items-center justify-center gap-1 mb-0.5" data-id="element-17116">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-17117" />
                        <span className="font-bold text-slate-900 text-sm" data-id="element-17118">
                          4.9
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-17119">
                        124 отзыва
                      </span>
                    </div>
                    <div data-id="element-17120">
                      <div className="font-bold text-slate-900 text-sm mb-0.5" data-id="element-17121">
                        8 лет
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-17122">Опыт</span>
                    </div>
                    <div data-id="element-17123">
                      <div className="flex items-center justify-center gap-1 mb-0.5" data-id="element-17124">
                        <Globe className="w-3.5 h-3.5 text-slate-400" data-id="element-17125" />
                        <span className="font-bold text-slate-900 text-sm" data-id="element-17126">
                          RU EN
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-17127">Языки</span>
                    </div>
                    <div data-id="element-17128">
                      <div className="flex items-center justify-center gap-1.5 mb-0.5" data-id="element-17129">
                        <Video className="w-3.5 h-3.5 text-indigo-500" data-id="element-17130" />
                        <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-17131" />
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-17132">
                        Форматы
                      </span>
                    </div>
                  </div>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-17133">
                    {['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия', 'Детский гороскоп'].map(tag => <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium" data-id="element-17134">
                        {tag}
                      </span>)}
                  </div>

                  {/* About — NO CONTACTS BLOCK HERE */}
                  <div className="pb-6" data-id="element-17135">
                    <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-17136">
                      Обо мне
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-2" data-id="element-17137">
                      Привет! Я Елена, профессиональный астролог с 8-летним
                      стажем. Моя цель — перевести сложный язык звёзд на
                      понятный человеческий. Я не пугаю ретроградным Меркурием,
                      а даю конкретные инструменты для улучшения качества жизни.
                    </p>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17138">
                      Использую методы классической западной астрологии +
                      психологию. Помогла 1000+ клиентам найти призвание,
                      наладить отношения и выйти из кризиса.
                    </p>
                  </div>
                </div>
              </div>
            </SectionErrorBoundary>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3" data-id="element-17139">
              {[{
              icon: Users,
              value: '1 250+',
              label: 'Консультаций',
              color: 'bg-indigo-100 text-indigo-600'
            }, {
              icon: Heart,
              value: '94%',
              label: 'Повторных',
              color: 'bg-emerald-100 text-emerald-600'
            }, {
              icon: Star,
              value: '5.0',
              label: 'Ср. оценка',
              color: 'bg-amber-100 text-amber-600'
            }].map(stat => <Card key={stat.label} padding="sm" className="flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-2.5 text-center sm:text-left" data-id="element-17140">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`} data-id="element-17141">
                    <stat.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-17142" />
                  </div>
                  <div data-id="element-17143">
                    <div className="text-sm sm:text-base font-bold text-slate-900 leading-tight" data-id="element-17144">
                      {stat.value}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-slate-500 font-medium" data-id="element-17145">
                      {stat.label}
                    </div>
                  </div>
                </Card>)}
            </div>

            {/* Services */}
            <SectionErrorBoundary sectionName="astrologer-services" data-id="element-17146">
              <Card padding="md" data-id="element-17147">
                <h2 className="text-lg font-bold text-slate-900 mb-3" data-id="element-17148">
                  Услуги
                </h2>
                <div className="divide-y divide-slate-100" data-id="element-17149">
                  {services.map((s, i) => <div key={i} className="py-3 first:pt-0 last:pb-0 group" data-id="element-17150">
                      <div className="hidden sm:flex items-center gap-3" data-id="element-17151">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${s.format === 'video' ? 'bg-indigo-50' : 'bg-emerald-50'}`} data-id="element-17152">
                          {s.format === 'video' ? <Video className="w-4 h-4 text-indigo-500" data-id="element-17153" /> : <MessageSquare className="w-4 h-4 text-emerald-500" data-id="element-17154" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-17155">
                          <div className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors" data-id="element-17156">
                            {s.title}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500" data-id="element-17157">
                            <span className="flex items-center" data-id="element-17158">
                              <Clock className="w-3 h-3 mr-0.5" data-id="element-17159" />
                              {s.duration}
                            </span>
                            <span data-id="element-17160">•</span>
                            <span data-id="element-17161">
                              {s.format === 'video' ? 'Видео' : 'Чат'}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-indigo-700 whitespace-nowrap" data-id="element-17162">
                          {s.price}
                        </span>
                        <Button variant="primary" size="sm" onClick={() => {
                      addItem({
                        id: `astrologer-service-${i}-${Date.now()}`,
                        name: `${s.title} — Елена Смирнова`,
                        description: s.desc,
                        price: parseInt(s.price.replace(/\s/g, '')),
                        type: 'consultation',
                        format: s.format === 'video' ? 'Видеозвонок' : 'Чат',
                        duration: s.duration,
                        badges: [{
                          label: 'Консультация',
                          variant: 'pro'
                        }]
                      });
                      onNavigate('checkout');
                    }} className="flex-shrink-0 text-xs px-3" data-id="element-17163">
                          Заказать
                        </Button>
                      </div>
                      <div className="sm:hidden" data-id="element-17164">
                        <div className="flex items-start gap-2.5 mb-2" data-id="element-17165">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${s.format === 'video' ? 'bg-indigo-50' : 'bg-emerald-50'}`} data-id="element-17166">
                            {s.format === 'video' ? <Video className="w-3.5 h-3.5 text-indigo-500" data-id="element-17167" /> : <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-17168" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-17169">
                            <div className="text-sm font-bold text-slate-900" data-id="element-17170">
                              {s.title}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5" data-id="element-17171">
                              <span className="flex items-center" data-id="element-17172">
                                <Clock className="w-3 h-3 mr-0.5" data-id="element-17173" />
                                {s.duration}
                              </span>
                              <span data-id="element-17174">•</span>
                              <span data-id="element-17175">
                                {s.format === 'video' ? 'Видео' : 'Чат'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between ml-[38px]" data-id="element-17176">
                          <span className="text-sm font-bold text-indigo-700" data-id="element-17177">
                            {s.price}
                          </span>
                          <Button variant="primary" size="sm" onClick={() => {
                        addItem({
                          id: `astrologer-service-${i}-${Date.now()}`,
                          name: `${s.title} — Елена Смирнова`,
                          description: s.desc,
                          price: parseInt(s.price.replace(/\s/g, '')),
                          type: 'consultation',
                          format: s.format === 'video' ? 'Видеозвонок' : 'Чат',
                          duration: s.duration,
                          badges: [{
                            label: 'Консультация',
                            variant: 'pro'
                          }]
                        });
                        onNavigate('checkout');
                      }} className="text-xs px-3" data-id="element-17178">
                            Заказать
                          </Button>
                        </div>
                      </div>
                    </div>)}
                </div>
              </Card>
            </SectionErrorBoundary>

            {/* Products */}
            <Card padding="md" data-id="element-17179">
              <div className="flex items-center justify-between mb-3" data-id="element-17180">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-17181">
                  <ShoppingBag className="w-4 h-4 text-indigo-500" data-id="element-17182" />
                  Товары
                </h2>
                <button className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center" onClick={() => onNavigate('product-detail')} data-id="element-17183">
                  Все <ChevronRight className="w-3 h-3 ml-0.5" data-id="element-17184" />
                </button>
              </div>
              <div className="divide-y divide-slate-100" data-id="element-17185">
                {products.map(p => <div key={p.id} className="flex items-center gap-2.5 py-2.5 first:pt-0 last:pb-0 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors" onClick={() => onNavigate('product-detail')} data-id="element-17186">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${p.color}`} data-id="element-17187">
                      <p.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-17188" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-17189">
                      <div className="text-xs sm:text-sm font-medium text-slate-900 truncate" data-id="element-17190">
                        {p.title}
                      </div>
                      <span className="text-[10px] text-slate-400 uppercase" data-id="element-17191">
                        {p.type}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-slate-900 whitespace-nowrap flex-shrink-0" data-id="element-17192">
                      {p.price}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" data-id="element-17193" />
                  </div>)}
              </div>
            </Card>

            {/* Education */}
            <Card padding="md" data-id="element-17194">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-17195">
                <GraduationCap className="w-5 h-5 text-indigo-500" data-id="element-17196" />
                Образование
              </h2>
              <div className="space-y-3" data-id="element-17197">
                {[{
                title: 'Московская Академия Астрологии',
                desc: 'Базовый и продвинутый курс • 2016-2018'
              }, {
                title: 'Школа Прогностики',
                desc: 'Глубокая прогностика и соляры • 2019'
              }].map((edu, i) => <div key={i} className="flex gap-3 items-start" data-id="element-17198">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-17199">
                      <Award className="w-4 h-4 text-slate-400" data-id="element-17200" />
                    </div>
                    <div data-id="element-17201">
                      <h4 className="text-sm font-bold text-slate-900" data-id="element-17202">
                        {edu.title}
                      </h4>
                      <p className="text-xs text-slate-500" data-id="element-17203">{edu.desc}</p>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Reviews */}
            <SectionErrorBoundary sectionName="astrologer-reviews" data-id="element-17204">
              <Card padding="md" data-id="element-17205">
                <div className="flex items-center justify-between mb-4" data-id="element-17206">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-17207">
                    Отзывы{' '}
                    <span className="text-slate-400 text-sm font-normal" data-id="element-17208">
                      (124)
                    </span>
                  </h2>
                  <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-lg" data-id="element-17209">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-17210" />
                    <span className="text-sm font-bold text-amber-700" data-id="element-17211">
                      4.9
                    </span>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-17212">
                  {visibleReviews.map(r => <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0" data-id="element-17213">
                      <div className="flex items-start justify-between mb-2" data-id="element-17214">
                        <div className="flex items-center gap-2.5" data-id="element-17215">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-bold text-indigo-700" data-id="element-17216">
                            {r.name[0]}
                          </div>
                          <div data-id="element-17217">
                            <div className="text-sm font-bold text-slate-900" data-id="element-17218">
                              {r.name}
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-17219">
                              {r.date} • {r.service}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5" data-id="element-17220">
                          {Array.from({
                        length: 5
                      }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-17221" />)}
                        </div>
                      </div>
                      <ReviewText text={r.text} maxLength={120} data-id="element-17222" />
                    </div>)}
                </div>
                {reviews.length > 3 && <Button variant="outline" size="sm" fullWidth className="mt-4" onClick={() => setShowAllReviews(!showAllReviews)} data-id="element-17223">
                    {showAllReviews ? 'Свернуть' : `Показать ещё ${reviews.length - 3} отзывов`}
                  </Button>}
              </Card>
            </SectionErrorBoundary>

            {/* FAQ */}
            <Card padding="md" data-id="element-17224">
              <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17225">
                Частые вопросы
              </h2>
              <Accordion items={faqs} data-id="element-17226" />
            </Card>
          </div>

          {/* RIGHT COLUMN: SIDEBAR */}
          <div className="lg:col-span-4" data-id="element-17227">
            <div className="sticky top-20 space-y-4" data-id="element-17228">
              {/* Booking Widget */}
              <Card padding="md" className="border-indigo-100 shadow-lg shadow-indigo-900/5" data-id="element-17229">
                <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-17230">
                  Запись на консультацию
                </h3>
                <div className="flex items-center gap-1.5 mb-4 pb-3 border-b border-slate-100" data-id="element-17231">
                  <span className="text-xs text-slate-500" data-id="element-17232">Стоимость:</span>
                  <span className="text-sm font-bold text-slate-900" data-id="element-17233">
                    от 4 000 до 6 000 ₽
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-3 font-medium" data-id="element-17234">
                  Выберите дату (МСК)
                </p>
                <div className="flex justify-between items-center mb-3 bg-slate-50 rounded-lg px-2 py-1.5" data-id="element-17235">
                  <button onClick={handlePrevMonth} className="p-0.5 text-slate-400 hover:text-slate-900 transition-colors" data-id="element-17236">
                    <ChevronDown className="w-4 h-4 rotate-90" data-id="element-17237" />
                  </button>
                  <span className="text-sm font-bold text-slate-900" data-id="element-17238">
                    {monthNames[currentMonth]} {currentYear}
                  </span>
                  <button onClick={handleNextMonth} className="p-0.5 text-slate-400 hover:text-slate-900 transition-colors" data-id="element-17239">
                    <ChevronDown className="w-4 h-4 -rotate-90" data-id="element-17240" />
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-4" data-id="element-17241">
                  {availableDays.map((day, i) => <div key={day.num} onClick={() => setSelectedDay(i)} className={`text-center py-2 px-1 rounded-lg border cursor-pointer transition-all ${i === selectedDay ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 hover:border-indigo-200 text-slate-600'}`} data-id="element-17242">
                      <div className="text-[9px] uppercase tracking-wider font-semibold" data-id="element-17243">
                        {day.label}
                      </div>
                      <div className="font-bold text-base" data-id="element-17244">{day.num}</div>
                    </div>)}
                </div>
                <div className="mb-5" data-id="element-17245">
                  <div className="text-xs font-bold text-slate-900 mb-2 flex items-center" data-id="element-17246">
                    <Clock className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-17247" />
                    Время:
                  </div>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-17248">
                    {timeSlots.map((time, i) => <button key={time} onClick={() => setSelectedTime(i)} className={`py-2 border rounded-lg text-sm font-medium transition-all ${i === selectedTime ? 'border-indigo-500 bg-indigo-500 text-white shadow-sm' : 'border-slate-200 text-slate-700 hover:border-indigo-300'}`} data-id="element-17249">
                        {time}
                      </button>)}
                  </div>
                </div>
                <Button variant="primary" size="lg" fullWidth onClick={() => {
                addItem({
                  id: `astrologer-booking-${Date.now()}`,
                  name: 'Консультация — Елена Смирнова',
                  description: `Онлайн-консультация, ${availableDays[selectedDay]?.label} ${availableDays[selectedDay]?.num} ${monthNames[currentMonth]}, ${timeSlots[selectedTime]}`,
                  price: 5000,
                  type: 'consultation',
                  format: 'Видеозвонок',
                  duration: '60 минут',
                  badges: [{
                    label: 'Консультация',
                    variant: 'pro'
                  }]
                });
                onNavigate('checkout');
              }} data-id="element-17250">
                  Продолжить оформление
                </Button>
                <div className="mt-3 flex items-center justify-center text-[11px] font-medium text-slate-500 bg-slate-50 py-1.5 rounded-lg" data-id="element-17251">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-500" data-id="element-17252" />
                  Безопасная сделка
                </div>
              </Card>

              {/* Location */}
              <Card padding="sm" className="bg-slate-50 border-none" data-id="element-17253">
                <div className="flex items-center gap-2.5" data-id="element-17254">
                  <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-17255">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" data-id="element-17256" />
                  </div>
                  <div data-id="element-17257">
                    <div className="text-sm font-bold text-slate-900" data-id="element-17258">
                      Москва
                    </div>
                    <div className="text-[11px] text-slate-500" data-id="element-17259">
                      Онлайн по всему миру
                    </div>
                  </div>
                </div>
              </Card>

              {/* Contacts — ONLY in sidebar */}
              <Card padding="sm" data-id="element-17260">
                <h3 className="text-sm font-bold text-slate-900 mb-3" data-id="element-17261">
                  Контакты
                </h3>
                <div className="space-y-2" data-id="element-17262">
                  <a href="https://t.me/elena_astro" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17263">
                    <span className="w-6 h-6 rounded-md bg-sky-100 flex items-center justify-center text-[9px] font-bold text-sky-600 flex-shrink-0" data-id="element-17264">
                      TG
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-sky-600 transition-colors" data-id="element-17265">
                      @elena_astro
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17266" />
                  </a>
                  <a href="https://wa.me/79991234567" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17267">
                    <span className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center text-[9px] font-bold text-emerald-600 flex-shrink-0" data-id="element-17268">
                      WA
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-emerald-600 transition-colors" data-id="element-17269">
                      WhatsApp
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17270" />
                  </a>
                  <a href="https://instagram.com/elena_stars" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17271">
                    <span className="w-6 h-6 rounded-md bg-pink-100 flex items-center justify-center text-[9px] font-bold text-pink-600 flex-shrink-0" data-id="element-17272">
                      IG
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-pink-600 transition-colors" data-id="element-17273">
                      elena_stars
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17274" />
                  </a>
                  <a href="https://elena-astro.ru" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17275">
                    <span className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-17276">
                      <Globe className="w-3 h-3 text-slate-500" data-id="element-17277" />
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-indigo-600 transition-colors" data-id="element-17278">
                      elena-astro.ru
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-300 ml-auto" data-id="element-17279" />
                  </a>
                  <a href="mailto:elena@astro.ru" className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-17280">
                    <span className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center text-[9px] font-bold text-blue-600 flex-shrink-0" data-id="element-17281">
                      @
                    </span>
                    <span className="text-xs font-medium text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-17282">
                      elena@astro.ru
                    </span>
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-8" data-id="element-17283">
          <AdSlot slotId="SLOT-12" onNavigate={onNavigate} data-id="element-17284" />
        </div>

        {/* Similar Astrologers */}
        <div className="mt-12 border-t border-slate-200 pt-8" data-id="element-17285">
          <div className="flex items-center justify-between mb-5" data-id="element-17286">
            <h2 className="text-lg font-bold text-slate-900" data-id="element-17287">
              Похожие астрологи
            </h2>
            <button onClick={() => onNavigate('astrologers')} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium hidden sm:flex items-center" data-id="element-17288">
              Все <ArrowRight className="w-3 h-3 ml-0.5" data-id="element-17289" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-17290">
            {similarAstrologers.map(a => <Card key={a.id} padding="sm" hoverable onClick={() => onNavigate('astrologer-profile')} data-id="element-17291">
                <div className="flex items-center gap-3" data-id="element-17292">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${a.avatar}`} data-id="element-17293">
                    {a.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-17294">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-17295">
                      <span className="text-sm font-bold text-slate-900 truncate" data-id="element-17296">
                        {a.name}
                      </span>
                      <BadgePill badge={a.badge} data-id="element-17297" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-17298">
                      <span className="flex items-center gap-0.5" data-id="element-17299">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-17300" />
                        {a.rating}
                      </span>
                      <span data-id="element-17301">от {a.price.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </div>
    </div>;
}